import {
  FieldMapping,
  FieldsResponse,
  HubSpotProperty,
  MappingsResponse,
  SaveMappingRequest,
  SyncRule,
  SyncRuleResponse,
  TokenValidationResponse,
  ValidationResponse,
  VeracrossCredentialsResponse
} from '../types/api';

const API_BASE_URL = import.meta.env.VITE_API_URL || 'http://localhost:3001';

interface RetryConfig {
  maxRetries: number;
  baseDelay: number;
  maxDelay: number;
  backoffMultiplier: number;
}

interface ConnectionState {
  isOnline: boolean;
  lastError: Error | null;
  retryCount: number;
}

class ApiClient {
  private baseUrl: string;
  private retryConfig: RetryConfig;
  private connectionState: ConnectionState;
  private activeRequests: Map<string, AbortController>;

  constructor(baseUrl: string = API_BASE_URL) {
    this.baseUrl = baseUrl;
    this.retryConfig = {
      maxRetries: 3,
      baseDelay: 1000, // 1 second
      maxDelay: 10000, // 10 seconds
      backoffMultiplier: 2
    };
    this.connectionState = {
      isOnline: true,
      lastError: null,
      retryCount: 0
    };
    this.activeRequests = new Map();
  }

  private isConnectionError(error: Error): boolean {
    const connectionErrorMessages = [
      'Failed to fetch',
      'NetworkError',
      'Connection refused',
      'ECONNREFUSED',
      'ENOTFOUND',
      'ETIMEDOUT',
      'ERR_NETWORK',
      'ERR_INTERNET_DISCONNECTED'
    ];
    
    return connectionErrorMessages.some(msg => 
      error.message.toLowerCase().includes(msg.toLowerCase())
    );
  }

  private calculateDelay(attempt: number): number {
    const delay = this.retryConfig.baseDelay * Math.pow(this.retryConfig.backoffMultiplier, attempt);
    return Math.min(delay, this.retryConfig.maxDelay);
  }

  private async sleep(ms: number): Promise<void> {
    return new Promise(resolve => setTimeout(resolve, ms));
  }

  private async requestWithRetry<T>(
    endpoint: string,
    options: RequestInit = {},
    attempt: number = 0
  ): Promise<T> {
    const requestId = `${endpoint}-${Date.now()}-${Math.random()}`;
    const controller = new AbortController();
    this.activeRequests.set(requestId, controller);

    try {
      const url = `${this.baseUrl}${endpoint}`;
      const config: RequestInit = {
        headers: {
          'Content-Type': 'application/json',
          ...options.headers,
        },
        signal: controller.signal,
        ...options,
      };

      const response = await fetch(url, config);
      
      // Remove from active requests on success
      this.activeRequests.delete(requestId);
      
      // Reset connection state on successful request
      this.connectionState = {
        isOnline: true,
        lastError: null,
        retryCount: 0
      };

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || `HTTP error! status: ${response.status}`);
      }

      return data;
    } catch (error) {
      this.activeRequests.delete(requestId);
      
      const isConnectionErr = this.isConnectionError(error as Error);
      
      if (isConnectionErr) {
        this.connectionState = {
          isOnline: false,
          lastError: error as Error,
          retryCount: attempt + 1
        };
      }

      // Retry logic for connection errors
      if (isConnectionErr && attempt < this.retryConfig.maxRetries) {
        const delay = this.calculateDelay(attempt);
        
        await this.sleep(delay);
        return this.requestWithRetry<T>(endpoint, options, attempt + 1);
      }

      // Don't retry for non-connection errors or after max retries
      throw error;
    }
  }

  private async request<T>(
    endpoint: string,
    options: RequestInit = {}
  ): Promise<T> {
    return this.requestWithRetry<T>(endpoint, options);
  }

  // Method to cancel all active requests
  public cancelAllRequests(): void {
    this.activeRequests.forEach(controller => controller.abort());
    this.activeRequests.clear();
  }

  // Method to get connection state
  public getConnectionState(): ConnectionState {
    return { ...this.connectionState };
  }

  // Method to manually reset connection state
  public resetConnectionState(): void {
    this.connectionState = {
      isOnline: true,
      lastError: null,
      retryCount: 0
    };
  }

  // Authentication
  async getHubSpotAuthUrl(): Promise<{ success: boolean; authUrl: string; state: string; error?: string }> {
    return this.request<{ success: boolean; authUrl: string; state: string; error?: string }>('/api/auth/hubspot-url');
  }

  async verifyToken(encryptedUserId: string): Promise<TokenValidationResponse> {
    return this.request<TokenValidationResponse>('/api/auth/verify-token', {
      method: 'POST',
      body: JSON.stringify({ encryptedUserId }),
    });
  }

  async logout(): Promise<{ success: boolean; message?: string; error?: string }> {
    return this.request<{ success: boolean; message?: string; error?: string }>('/api/auth/logout', {
      method: 'POST',
    });
  }

  // Fields API - combines both HubSpot properties and Veracross fields
  async getFields(portalId: number, objectType: 'contacts' | 'deals'): Promise<FieldsResponse> {
    return this.request<FieldsResponse>(
      `/api/fields?objectType=${encodeURIComponent(objectType)}`,
      {
        method: 'GET',
        headers: {
          'x-portal-id': portalId.toString()
        }
      }
    );
  }

  // HubSpot properties API - for field mapping dropdown (supports contacts and deals)
  async getHubSpotProperties(portalId: number, encryptedUserId: string, objectType: 'contacts' | 'deals'): Promise<{
    success: boolean;
    objectType: string;
    properties: HubSpotProperty[];
    source: string;
    note: string;
    error?: string;
  }> {
    return this.request<{
      success: boolean;
      objectType: string;
      properties: HubSpotProperty[];
      source: string;
      note: string;
      error?: string;
    }>(`/api/hubspot/properties?objectType=${encodeURIComponent(objectType)}`, {
      method: 'GET',
      headers: {
        'x-portal-id': portalId.toString(),
        'x-user-id': encryptedUserId
      }
    });
  }

  // Veracross fields only - returns handcoded field values
  async getVeracrossFields(encryptedUserId: string, objectType: 'contacts' | 'deals' | 'applicant' | 'application' | 'relative'): Promise<{ success: boolean; objectType: string; fields: any[]; source: string; note: string }> {
    return this.request<{ success: boolean; objectType: string; fields: any[]; source: string; note: string }>(
      `/api/veracross/fields?objectType=${encodeURIComponent(objectType)}`,
      {
        method: 'GET',
        headers: {
          'x-user-id': encryptedUserId
        }
      }
    );
  }

  // Property groups API
  async getPropertyGroups(portalId: number, encryptedUserId: string, objectType: 'contacts' | 'deals'): Promise<{ success: boolean; objectType: string; options: Array<{ value: string; label: string }> }> {
    return this.request<{ success: boolean; objectType: string; options: Array<{ value: string; label: string }> }>(
      `/api/dropdown/property-groups?objectType=${encodeURIComponent(objectType)}`,
      {
        method: 'GET',
        headers: {
          'x-portal-id': portalId.toString(),
          'x-user-id': encryptedUserId
        }
      }
    );
  }

  // Create custom property API
  async createCustomProperty(portalId: number, encryptedUserId: string, objectType: string, propertyName: string, propertyGroup: string, propertyType: string): Promise<{
    success: boolean;
    property?: any;
    message?: string;
    error?: string;
  }> {
    return this.request<{
      success: boolean;
      property?: any;
      message?: string;
      error?: string;
    }>('/api/hubspot/custom-property', {
      method: 'POST',
      headers: {
        'x-portal-id': portalId.toString(),
        'x-user-id': encryptedUserId
      },
      body: JSON.stringify({
        objectType,
        propertyName,
        propertyGroup,
        propertyType,
      }),
    });
  }

  // Veracross token exchange
  async exchangeVeracrossTokens(
    encryptedUserId: string,
    credentials: { clientId: string; clientSecret: string; baseUrl: string }
  ): Promise<VeracrossCredentialsResponse> {
    return this.request<VeracrossCredentialsResponse>('/api/veracross/credentials', {
      method: 'POST',
      body: JSON.stringify({ encryptedUserId, credentials }),
    });
  }

  // Check existing Veracross credentials
  async checkExistingVeracrossCredentials(encryptedUserId: string): Promise<{
    success: boolean;
    hasExistingUser: boolean;
    hasValidToken: boolean;
    userInfo: {
      email: string;
      portalId: number;
    };
    tokenInfo?: {
      expiresAt?: Date;
      scopes: string[];
      createdAt: Date;
    };
    existingEncryptedUserId?: string;
    tokenRefreshed?: boolean;
    needsReauth?: boolean;
    error?: string;
  }> {
    return this.request<{
      success: boolean;
      hasExistingUser: boolean;
      hasValidToken: boolean;
      userInfo: {
        email: string;
        portalId: number;
    };
      tokenInfo?: {
        expiresAt?: Date;
        scopes: string[];
        createdAt: Date;
      };
      existingEncryptedUserId?: string;
      tokenRefreshed?: boolean;
      needsReauth?: boolean;
      error?: string;
    }>(`/api/veracross/check-existing?encryptedUserId=${encodeURIComponent(encryptedUserId)}`);
  }

  // Check user onboarding status (both HubSpot and Veracross tokens)
  async checkUserOnboardingStatus(encryptedUserId: string): Promise<{
    success: boolean;
    hasBothTokens: boolean;
    hasHubSpotToken: boolean;
    hasVeracrossToken: boolean;
    userInfo?: {
      portalId: number;
      email: string;
    };
    error?: string;
  }> {
    return this.request<{
      success: boolean;
      hasBothTokens: boolean;
      hasHubSpotToken: boolean;
      hasVeracrossToken: boolean;
      userInfo?: {
        portalId: number;
        email: string;
      };
      error?: string;
    }>(`/api/auth/onboarding-status?encryptedUserId=${encodeURIComponent(encryptedUserId)}`);
  }

  // Get comprehensive authentication status (recommended)
  async getAuthenticationStatus(encryptedUserId: string): Promise<{
    success: boolean;
    user: {
      portalId: number;
      email: string;
    };
    hubspot: {
      status: 'valid' | 'expired' | 'missing';
      hasToken: boolean;
      expiresAt?: Date;
      canRefresh: boolean;
    };
    veracross: {
      status: 'valid' | 'expired' | 'missing';
      hasToken: boolean;
      expiresAt?: Date;
      scopes?: string[];
      canRefresh: boolean;
    };
    overall: {
      isFullyAuthenticated: boolean;
      missingServices: string[];
      needsAction: string[];
    };
    error?: string;
  }> {
    return this.request<{
      success: boolean;
      user: {
        portalId: number;
        email: string;
      };
      hubspot: {
        status: 'valid' | 'expired' | 'missing';
        hasToken: boolean;
        expiresAt?: Date;
        canRefresh: boolean;
      };
      veracross: {
        status: 'valid' | 'expired' | 'missing';
        hasToken: boolean;
        expiresAt?: Date;
        scopes?: string[];
        canRefresh: boolean;
      };
      overall: {
        isFullyAuthenticated: boolean;
        missingServices: string[];
        needsAction: string[];
      };
      error?: string;
    }>(`/api/auth/status?encryptedUserId=${encodeURIComponent(encryptedUserId)}`);
  }



  // Mappings API - simplified approach

  async getMappings(portalId: number, encryptedUserId: string, objectType: 'relative' | 'applicant' | 'application'): Promise<MappingsResponse> {
    const params = new URLSearchParams();
    params.append('objectType', objectType);

    return this.request<MappingsResponse>(
      `/api/mappings?${params.toString()}`,
      {
        method: 'GET',
        headers: {
          'x-portal-id': portalId.toString(),
          'x-user-id': encryptedUserId
        }
      }
    );
  }

  async updateMapping(portalId: number, encryptedUserId: string, mappingId: string, updates: Partial<FieldMapping>): Promise<ValidationResponse> {
    return this.request<ValidationResponse>(`/api/mappings/${mappingId}`, {
      method: 'PUT',
      headers: {
        'x-portal-id': portalId.toString(),
        'x-user-id': encryptedUserId
      },
      body: JSON.stringify(updates),
    });
  }

  async deleteMapping(portalId: number, encryptedUserId: string, mappingId: string): Promise<ValidationResponse> {
    return this.request<ValidationResponse>(`/api/mappings/${mappingId}`, {
      method: 'DELETE',
      headers: {
        'x-portal-id': portalId.toString(),
        'x-user-id': encryptedUserId
      }
    });
  }

  // Mapping counts for success page
  async getMappingCounts(portalId: number, encryptedUserId: string): Promise<{
    success: boolean;
    counts: {
      contact: number;
      deal: number;
      total: number;
    };
    error?: string;
  }> {
    return this.request<{
      success: boolean;
      counts: {
        contact: number;
        deal: number;
        total: number;
      };
      error?: string;
    }>(`/api/mappings/counts`, {
      method: 'GET',
      headers: {
        'x-portal-id': portalId.toString(),
        'x-user-id': encryptedUserId
      }
    });
  }


  // Save mappings to database
  async saveMappings(portalId: number, encryptedUserId: string, request: Omit<SaveMappingRequest, 'portalId'>): Promise<{
    success: boolean;
    message?: string;
    error?: string;
  }> {
    const requestOptions = {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'x-portal-id': portalId.toString(),
        'x-user-id': encryptedUserId
      },
      body: JSON.stringify(request),
    };

    return this.request<{
      success: boolean;
      message?: string;
      error?: string;
    }>('/api/mappings', requestOptions);
  }

  // Get sync rules
  async getSyncRules(portalId: number, encryptedUserId: string): Promise<SyncRuleResponse> {
    return this.request<SyncRuleResponse>(
      `/api/sync-rules`,
      {
        method: 'GET',
        headers: {
          'x-portal-id': portalId.toString(),
          'x-user-id': encryptedUserId
        }
      }
    );
  }

  // Save sync rules
  async saveSyncRules(portalId: number, encryptedUserId: string, syncRules: SyncRule): Promise<{
    success: boolean;
    message?: string;
    error?: string;
  }> {
    return this.request<{
      success: boolean;
      message?: string;
      error?: string;
    }>('/api/sync-rules', {
      method: 'POST',
      headers: {
        'x-portal-id': portalId.toString(),
        'x-user-id': encryptedUserId
      },
      body: JSON.stringify(syncRules),
    });
  }
}

export const apiClient = new ApiClient();